<template>
  <div class="tw:flex tw:flex-col tw:gap-2">
    <div
      v-for="(sortOption, index) in modelValue"
      :key="index"
      class="tw:flex tw:justify-between"
    >
      <SortOrderPicker
        :data-testid="`sort-order-picker-${index}`"
        :disabled="disabled"
        :exclude-sort-option-keys="excludedSortingOptions"
        :label="
          single
            ? getLocaleText('sorting_expandable.sort_by')
            : getLocaleText('sorting_expandable.sort_by_ordinal', {
                ordinal: ordinalNumber(index + 1),
              })
        "
        :model-value="modelValue[index]"
        @update:model-value="updateSortOrder($event, index)"
      />
      <BaseButton
        v-if="modelValue.length > 1"
        :disabled="disabled"
        icon="bin"
        new-design
        new-icon
        size="sm"
        @click="removeSortingOption(index)"
      />
    </div>
    <div
      v-if="!single"
      class="tw:flex"
    >
      <BaseButton
        :aria-label="getLocaleText('sorting_expandable.add_sorting')"
        data-testid="add-sorting-button"
        :disabled="disabled"
        icon="plus"
        new-design
        new-icon
        size="sm"
        :title="getLocaleText('sorting_expandable.add_sorting')"
        @click="addSortingOptions"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { watch } from "vue";

import { getLocaleText } from "shared/boot/i18n";
import BaseButton from "shared/components/base/BaseButton";
import SortOrderPicker from "shared/components/core/pickers/SortOrderPicker.vue";
import {
  SortOptionField,
  SortOptionMissing,
  SortOptionOrder,
} from "shared/helpers/mentions";
import { ordinalNumber } from "shared/helpers/number";
import { type StreamFiltersSortOption } from "shared/helpers/StreamFilters";

interface SortingExpandableProps {
  disabled?: boolean;
  excludedSortingOptions: SortOptionField[];
  single?: boolean;
}

const modelValue = defineModel<StreamFiltersSortOption[]>({
  default: () => [],
});

defineProps<SortingExpandableProps>();

watch(
  modelValue,
  (sortOptions) => {
    if (!sortOptions.length) {
      addSortingOptions();
    }
  },
  {
    immediate: true,
  }
);

function addSortingOptions(): void {
  modelValue.value = [
    ...modelValue.value,
    {
      sortBy: SortOptionField.TIMESTAMP,
      orderBy: SortOptionOrder.DESC,
      missing: SortOptionMissing.LAST,
    },
  ];
}

function removeSortingOption(index: number): void {
  const options = modelValue.value;
  options.splice(index, 1);
  modelValue.value = [...options];
}

function updateSortOrder(value: StreamFiltersSortOption, index: number): void {
  modelValue.value = modelValue.value.toSpliced(index, 1, value);
}
</script>
